import { FormEvent, useState, useTransition } from "react";
import {
  bulkCreateQuestions,
  bulkCreateQuizzes,
  bulkCreateUsers,
  bulkDeleteEntities,
  exportToCsv,
  getCsvTemplate,
  importFromCsv,
} from "@/lib/api";
import type {
  BulkQuestionCreateRequest,
  BulkQuizCreateRequest,
  BulkUserCreateRequest,
  BulkOperationResult,
  CsvExportRequest,
  CsvImportRequest,
} from "@/types/api";

type MessageState = {
  tone: "success" | "error";
  text: string;
};

const defaultUsersSeed = `[
  {
    "email": "analyst@example.com",
    "username": "analyst01",
    "full_name": "Blue Team Analyst",
    "password": "ChangeMe123!"
  }
]`;

const defaultQuestionsSeed = `{
  "questions": [
    {
      "text": { "en": "Identify the first response step after detecting malware." },
      "cooldown": 5,
      "time": 45,
      "marks": 10,
      "options": [
        { "text": { "en": "Isolate affected host" }, "is_correct": true },
        { "text": { "en": "Notify executive leadership" }, "is_correct": false }
      ]
    }
  ]
}`;

const defaultQuizzesSeed = `{
  "quizzes": [
    {
      "name": "Incident Response Fundamentals",
      "description": "Core triage playbook knowledge check",
      "questions": [
        {
          "text": { "en": "What is the primary objective of initial triage?" },
          "cooldown": 5,
          "time": 30,
          "marks": 10,
          "options": [
            { "text": { "en": "Scope validation" }, "is_correct": true },
            { "text": { "en": "Root cause analysis" }, "is_correct": false }
          ]
        }
      ]
    }
  ]
}`;

export function BulkOperationsPanel() {
  const [message, setMessage] = useState<MessageState | null>(null);
  const [isPending, startTransition] = useTransition();
  const [lastResult, setLastResult] = useState<BulkOperationResult | null>(null);

  const [usersPayload, setUsersPayload] = useState(defaultUsersSeed);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState("");

  const [questionsPayload, setQuestionsPayload] = useState(
    defaultQuestionsSeed,
  );
  const [quizzesPayload, setQuizzesPayload] = useState(defaultQuizzesSeed);

  const [entityType, setEntityType] = useState("quizzes");
  const [deleteIds, setDeleteIds] = useState("");
  const [forceDelete, setForceDelete] = useState(false);

  const [csvEntity, setCsvEntity] = useState("users");
  const [csvData, setCsvData] = useState("email,username\nuser@example.com,user1");
  const [csvMapping, setCsvMapping] = useState('{"email": "email"}');

  const [exportPayload, setExportPayload] = useState<CsvExportRequest>({
    entity_type: "users",
    include_analytics: false,
  });
  const [exportResult, setExportResult] = useState<string | null>(null);

  const showMessage = (tone: "success" | "error", text: string) => {
    setMessage({ tone, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleBulkUsers = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      try {
        const parsed = JSON.parse(usersPayload) as BulkUserCreateRequest["users"];
        const response = await bulkCreateUsers({
          users: parsed,
          send_welcome_email: sendWelcomeEmail,
          default_password: defaultPassword || undefined,
        });
        if (!response) {
          showMessage("error", "User import failed. Validate payload structure.");
          setLastResult(null);
          return;
        }
        setLastResult(response);
        showMessage(
          "success",
          `Processed ${response.total_processed} users. Successful: ${response.successful}.`,
        );
      } catch (error) {
        console.error(error);
        showMessage("error", "Invalid JSON payload supplied.");
        setLastResult(null);
      }
    });
  };

  const handleBulkQuestions = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      try {
        const parsed = JSON.parse(questionsPayload) as BulkQuestionCreateRequest;
        const response = await bulkCreateQuestions(parsed);
        if (!response) {
          showMessage("error", "Question ingestion failed.");
          setLastResult(null);
          return;
        }
        setLastResult(response);
        showMessage(
          "success",
          `Questions processed: ${response.total_processed}. Success: ${response.successful}.`,
        );
      } catch (error) {
        console.error(error);
        showMessage("error", "Invalid JSON for question import.");
        setLastResult(null);
      }
    });
  };

  const handleBulkQuizzes = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      try {
        const parsed = JSON.parse(quizzesPayload) as BulkQuizCreateRequest;
        const response = await bulkCreateQuizzes(parsed);
        if (!response) {
          showMessage("error", "Quiz import failed.");
          setLastResult(null);
          return;
        }
        setLastResult(response);
        showMessage(
          "success",
          `Quizzes processed: ${response.total_processed}. Success: ${response.successful}.`,
        );
      } catch (error) {
        console.error(error);
        showMessage("error", "Invalid JSON for quiz import.");
        setLastResult(null);
      }
    });
  };

  const handleBulkDelete = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const ids = deleteIds
      .split("\n")
      .map((id) => id.trim())
      .filter(Boolean);
    if (ids.length === 0) {
      showMessage("error", "Provide at least one ID to delete.");
      return;
    }
    startTransition(async () => {
      const response = await bulkDeleteEntities(entityType, {
        ids,
        force_delete: forceDelete,
      });
      if (!response) {
        showMessage("error", "Bulk delete failed.");
        setLastResult(null);
        return;
      }
      setLastResult(response);
      showMessage(
        "success",
        `Deletion attempted for ${response.total_processed} entities. Success: ${response.successful}.`,
      );
    });
  };

  const handleCsvImport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      try {
        const mapping = JSON.parse(csvMapping) as CsvImportRequest["mapping"];
        const response = await importFromCsv({
          entity_type: csvEntity,
          csv_data: csvData,
          mapping,
          skip_header: true,
        });
        if (!response) {
          showMessage("error", "CSV import failed.");
          setLastResult(null);
          return;
        }
        setLastResult(response);
        showMessage(
          "success",
          `CSV processed: ${response.total_processed}. Success: ${response.successful}.`,
        );
      } catch (error) {
        console.error(error);
        showMessage("error", "Invalid mapping JSON for CSV import.");
        setLastResult(null);
      }
    });
  };

  const handleCsvTemplate = async () => {
    startTransition(async () => {
      const template = await getCsvTemplate(csvEntity);
      if (!template) {
        showMessage("error", "Unable to retrieve CSV template.");
        return;
      }
      setCsvData(template);
      showMessage("success", "CSV template loaded. Customize and import.");
    });
  };

  const handleCsvExport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      const response = await exportToCsv(exportPayload);
      if (!response) {
        showMessage("error", "CSV export failed.");
        return;
      }
      setLastResult(null);
      setExportResult(response);
      showMessage("success", "CSV export ready. Copy from the panel below.");
    });
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900">
          Bulk automation workspace
        </h3>
        <p className="mt-1 text-sm text-neutral-500">
          Execute high-volume operations. JSON payloads follow the OpenAPI
          contract.
        </p>
      </div>

      {message ? (
        <p
          className={`mt-4 rounded-md border px-4 py-2 text-sm ${
            message.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-600"
          }`}
        >
          {message.text}
        </p>
      ) : null}

      {lastResult ? (
        <div className="mt-4 rounded-md border border-neutral-200 bg-neutral-50 p-4 text-xs text-neutral-600">
          <p className="text-sm font-semibold text-neutral-800">Last operation</p>
          <div className="mt-2 flex flex-wrap gap-4 text-neutral-700">
            <span>Total processed: {lastResult.total_processed}</span>
            <span>Successful: {lastResult.successful}</span>
            <span className="text-red-600">Failed: {lastResult.failed}</span>
            {lastResult.created_ids?.length ? (
              <span>Created IDs: {lastResult.created_ids.slice(0, 5).join(", ")}{lastResult.created_ids.length > 5 ? "…" : ""}</span>
            ) : null}
          </div>
          {lastResult.errors?.length ? (
            <details className="mt-3 rounded border border-neutral-200 bg-white px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold text-neutral-700">
                View errors ({lastResult.errors.length})
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-neutral-900/5 p-3 text-[11px]">
                {JSON.stringify(lastResult.errors, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 space-y-6">
        <form
          onSubmit={handleBulkUsers}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800">
                Bulk create users
              </h4>
              <p className="text-xs text-neutral-500">
                Paste a JSON array following the `UserCreateSchema`.
              </p>
            </div>
            <label className="text-xs text-neutral-600">
              <input
                type="checkbox"
                checked={sendWelcomeEmail}
                onChange={(event) => setSendWelcomeEmail(event.target.checked)}
                className="mr-2 h-4 w-4 rounded border-neutral-300"
              />
              Send welcome email
            </label>
          </div>
          <textarea
            value={usersPayload}
            onChange={(event) => setUsersPayload(event.target.value)}
            rows={8}
            className="mt-3 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm font-mono focus:border-neutral-400 focus:outline-none"
          />
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex w-full items-center justify-between gap-3 text-xs text-neutral-600 sm:w-auto">
              Default password (optional)
              <input
                type="text"
                value={defaultPassword}
                onChange={(event) => setDefaultPassword(event.target.value)}
                className="rounded-md border border-neutral-200 px-2 py-1 text-xs focus:border-neutral-400 focus:outline-none"
                placeholder="If blank, use payload password"
              />
            </label>
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {isPending ? "Processing..." : "Run user import"}
            </button>
          </div>
        </form>

        <form
          onSubmit={handleBulkQuestions}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800">
                Bulk create questions
              </h4>
              <p className="text-xs text-neutral-500">
                Payload adheres to `BulkQuestionCreateRequest`.
              </p>
            </div>
          </div>
          <textarea
            value={questionsPayload}
            onChange={(event) => setQuestionsPayload(event.target.value)}
            rows={8}
            className="mt-3 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm font-mono focus:border-neutral-400 focus:outline-none"
          />
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {isPending ? "Processing..." : "Run question import"}
            </button>
          </div>
        </form>

        <form
          onSubmit={handleBulkQuizzes}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800">
                Bulk create quizzes
              </h4>
              <p className="text-xs text-neutral-500">
                Ensure nested questions follow the `QuestionSchema-Input`.
              </p>
            </div>
          </div>
          <textarea
            value={quizzesPayload}
            onChange={(event) => setQuizzesPayload(event.target.value)}
            rows={8}
            className="mt-3 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm font-mono focus:border-neutral-400 focus:outline-none"
          />
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {isPending ? "Processing..." : "Run quiz import"}
            </button>
          </div>
        </form>

        <form
          onSubmit={handleBulkDelete}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800">
                Bulk delete entities
              </h4>
              <p className="text-xs text-neutral-500">
                Provide newline-delimited IDs for quizzes, questions, rooms, or
                users.
              </p>
            </div>
            <label className="text-xs text-neutral-600">
              <input
                type="checkbox"
                checked={forceDelete}
                onChange={(event) => setForceDelete(event.target.checked)}
                className="mr-2 h-4 w-4 rounded border-neutral-300"
              />
              Force delete
            </label>
          </div>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <select
              value={entityType}
              onChange={(event) => setEntityType(event.target.value)}
              className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none sm:w-44"
            >
              <option value="quizzes">Quizzes</option>
              <option value="questions">Questions</option>
              <option value="users">Users</option>
              <option value="rooms">Rooms</option>
            </select>
            <textarea
              value={deleteIds}
              onChange={(event) => setDeleteIds(event.target.value)}
              rows={4}
              className="flex-1 rounded-md border border-neutral-200 px-3 py-2 text-sm font-mono focus:border-neutral-400 focus:outline-none"
              placeholder="Enter one ID per line"
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-70"
            >
              {isPending ? "Processing..." : "Delete entities"}
            </button>
          </div>
        </form>

        <form
          onSubmit={handleCsvImport}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800">
                CSV import
              </h4>
              <p className="text-xs text-neutral-500">
                Supply CSV data and mapping for the target entity type.
              </p>
            </div>
            <button
              type="button"
              onClick={handleCsvTemplate}
              className="rounded-md border border-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
            >
              Load template
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <select
              value={csvEntity}
              onChange={(event) => setCsvEntity(event.target.value)}
              className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
            >
              <option value="users">Users</option>
              <option value="questions">Questions</option>
              <option value="quizzes">Quizzes</option>
            </select>
            <textarea
              value={csvMapping}
              onChange={(event) => setCsvMapping(event.target.value)}
              rows={3}
              className="rounded-md border border-neutral-200 px-3 py-2 text-xs font-mono focus:border-neutral-400 focus:outline-none"
              placeholder='{"email": "email"}'
            />
          </div>
          <textarea
            value={csvData}
            onChange={(event) => setCsvData(event.target.value)}
            rows={6}
            className="mt-3 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm font-mono focus:border-neutral-400 focus:outline-none"
          />
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {isPending ? "Processing..." : "Import CSV"}
            </button>
          </div>
        </form>

        <form
          onSubmit={handleCsvExport}
          className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800">
                CSV export
              </h4>
              <p className="text-xs text-neutral-500">
                Use OpenAPI export contract to retrieve bulk data snapshots.
              </p>
            </div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-neutral-600">Entity type</span>
              <select
                value={exportPayload.entity_type}
                onChange={(event) =>
                  setExportPayload((prev) => ({
                    ...prev,
                    entity_type: event.target.value,
                  }))
                }
                className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
              >
                <option value="users">Users</option>
                <option value="quizzes">Quizzes</option>
                <option value="questions">Questions</option>
                <option value="rooms">Rooms</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-neutral-600">Fields (comma separated)</span>
              <input
                type="text"
                value={exportPayload.fields?.join(",") ?? ""}
                onChange={(event) =>
                  setExportPayload((prev) => ({
                    ...prev,
                    fields: event.target.value
                      ? event.target.value.split(",").map((field) => field.trim())
                      : undefined,
                  }))
                }
                className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                placeholder="email,username"
              />
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-neutral-600">
              <input
                type="checkbox"
                checked={Boolean(exportPayload.include_analytics)}
                onChange={(event) =>
                  setExportPayload((prev) => ({
                    ...prev,
                    include_analytics: event.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-neutral-300"
              />
              Include analytics
            </label>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {isPending ? "Processing..." : "Export CSV"}
            </button>
          </div>

          {exportResult ? (
            <div className="mt-4">
              <p className="text-xs font-semibold text-neutral-600">
                Export output
              </p>
              <textarea
                value={exportResult}
                readOnly
                rows={6}
                className="mt-2 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm font-mono"
              />
            </div>
          ) : null}
        </form>
      </div>
    </section>
  );
}


